import { useState, useEffect } from "react";
import { Link } from "react-router";
import { Wordmark } from "../../components/brand/Wordmark";
import { DataCoverageNotice } from "../../components/brand/DataCoverageNotice";
import { Card } from "../../components/ui/Card";
import { ROUTES } from "../../lib/constants";
import { api } from "../../api/client";

interface SampleWine {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  score: number | null;
  abv: string | null;
  avgPrice: string | null;
}

const features = [
  {
    title: "Wine Search",
    description:
      "Search the growing wine catalog by vintage, region, grape variety, and type. Descriptions, scores, and prices are available where recorded.",
    endpoint: "GET /wines/search?q=",
  },
  {
    title: "Price Data",
    description:
      "Explore recorded prices where available. Coverage is limited; many wines have no prices, and records are not guaranteed to be current retail offers.",
    endpoint: "GET /wines/:id/prices",
  },
  {
    title: "Food Pairings",
    description:
      "Explore available food pairing suggestions, with confidence scores. Coverage varies by wine.",
    endpoint: "GET /wines/:id/pairings",
  },
  {
    title: "Label Recognition",
    description:
      "Upload a wine label photo to find potential matches. Results depend on image quality and catalog coverage.",
    endpoint: "POST /identify/image",
  },
  {
    title: "Tasting Notes",
    description:
      "Explore recorded critic scores and tasting notes where available. Not every wine has review data.",
    endpoint: "GET /wines/:id",
  },
  {
    title: "Reference Data",
    description:
      "Browse the growing collection of wine regions and grape varieties. Reference coverage is not exhaustive.",
    endpoint: "GET /regions, /grapes",
  },
];

export function LandingPage() {
  const [wine, setWine] = useState<SampleWine | null>(null);

  useEffect(() => {
    api<SampleWine>("/wines/sample")
      .then((w) => setWine(w))
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <Wordmark size="lg" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-medium text-garnet tracking-tight mb-4">
            Wine intelligence, served via API.
          </h1>
          <p className="font-display text-lg italic text-terroir mb-10">
            A growing source of wine knowledge, via API.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href={ROUTES.DOCS}
              className="inline-flex min-h-11 items-center px-6 py-3 border border-barrel text-barrel font-medium text-sm rounded-md hover:bg-barrel/5 transition-colors no-underline"
            >
              View API Docs
            </a>
            <Link
              to={ROUTES.LOGIN}
              className="inline-flex min-h-11 items-center px-6 py-3 bg-barrel text-white font-medium text-sm rounded-md hover:bg-garnet transition-colors no-underline"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </section>

      {/* Code example — sample API response card */}
      {wine && (
        <section className="px-6 pb-20">
          <div className="max-w-2xl mx-auto">
            <Card className="shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-display text-[17px] font-medium text-noir">
                    {wine.name}
                  </h3>
                  <p className="text-xs text-terroir mt-0.5">
                    {wine.subtitle}
                  </p>
                </div>
                {wine.score != null && (
                  <div className="bg-parchment border-[0.5px] border-barrel rounded-md px-2.5 py-1 text-center shrink-0 ml-4">
                    <div className="text-lg font-medium text-garnet leading-tight">
                      {wine.score}
                    </div>
                    <div className="text-[10px] text-terroir">pts</div>
                  </div>
                )}
              </div>
              {wine.description && (
                <div className="pt-3 mb-3">
                  <p className="text-xs text-noir/70 leading-relaxed line-clamp-3">
                    {wine.description}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mb-3">
                {[
                  { label: "ABV", value: wine.abv },
                  { label: "Avg price", value: wine.avgPrice },
                ].map((item) => (
                  <div key={item.label} className="bg-parchment rounded-md p-2">
                    <div className="text-[10px] text-terroir">{item.label}</div>
                    <div className="text-sm font-medium text-noir">
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-noir rounded-md px-3 py-2">
                <code className="font-mono text-[11px] text-barrel">
                  GET{" "}
                  <span className="text-garnet-light">
                    /v1/wines/{wine.id}
                  </span>
                </code>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="px-6 pb-20">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl font-medium text-garnet text-center mb-2">
            Build with a growing wine dataset
          </h2>
          <p className="text-sm text-terroir text-center mb-10">
            Search, explore, and integrate wine data. Availability varies by wine and feature.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[10px]">
            {features.map((f) => (
              <Card key={f.title}>
                <h3 className="text-sm font-medium text-noir mb-1">
                  {f.title}
                </h3>
                <p className="text-xs text-terroir leading-relaxed mb-3">
                  {f.description}
                </p>
                <code className="font-mono text-[11px] text-barrel bg-parchment px-2 py-1 rounded">
                  {f.endpoint}
                </code>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-6 pb-24">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl font-medium text-garnet text-center mb-2">
            Simple, transparent pricing
          </h2>
          <p className="text-sm text-terroir text-center mb-10">
            Start free. Upgrade when you're ready.
          </p>
          <DataCoverageNotice />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[10px]">
            {/* Free */}
            <Card className="flex flex-col">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-noir">Free</h3>
                <div className="mt-2">
                  <span className="text-3xl font-medium text-noir">$0</span>
                  <span className="text-sm text-terroir ml-1">/month</span>
                </div>
              </div>
              <ul className="space-y-2">
                {[
                  "100 requests per day",
                  "Wine search & details",
                  "Price lookups (limited coverage)",
                  "Food pairings",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm text-noir/80"
                  >
                    <svg
                      className="w-4 h-4 text-cellar shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-3 mb-auto text-xs text-terroir italic">
                Personal use only — not for commercial or business use.
              </p>
              <Link
                to={ROUTES.LOGIN}
                className="block min-h-11 text-center px-4 py-3 border border-barrel text-barrel text-sm font-medium rounded-md hover:bg-barrel/5 transition-colors no-underline"
              >
                Sign Up Free
              </Link>
            </Card>

            {/* Supporter */}
            <Card className="flex flex-col border-barrel/40 ring-1 ring-barrel/20">
              <div className="mb-4">
                <div className="flex items-center gap-2"><h3 className="text-lg font-medium text-noir">Supporter</h3><span className="text-[10px] font-medium bg-barrel text-white px-2 py-0.5 rounded">RECOMMENDED</span></div>
                <div className="mt-2">
                  <span className="text-3xl font-medium text-noir">$25</span>
                  <span className="text-sm text-terroir ml-1">/month</span>
                </div>
              </div>
              <ul className="space-y-2 mb-auto">
                {[
                  "250 requests per day",
                  "Everything in Free",
                  "Support the project",
                  "Business use license",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm text-noir/80"
                  >
                    <svg
                      className="w-4 h-4 text-cellar shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to={ROUTES.LOGIN}
                className="block min-h-11 text-center px-4 py-3 bg-barrel text-white text-sm font-medium rounded-md hover:bg-garnet transition-colors no-underline"
              >
                Get Started
              </Link>
            </Card>

            {/* Pro */}
            <Card className="flex flex-col">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-noir">Pro</h3>
                <div className="mt-2">
                  <span className="text-3xl font-medium text-noir">$100</span>
                  <span className="text-sm text-terroir ml-1">/month</span>
                </div>
              </div>
              <ul className="space-y-2 mb-auto">
                {[
                  "1,000 requests per day",
                  "Everything in Free",
                  "Label recognition (image)",
                  "Smart identification",
                  "Price history access (limited coverage)",
                  "Priority support",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm text-noir/80"
                  >
                    <svg
                      className="w-4 h-4 text-cellar shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to={ROUTES.LOGIN}
                className="block min-h-11 text-center px-4 py-3 border border-barrel text-barrel text-sm font-medium rounded-md hover:bg-barrel/5 transition-colors no-underline"
              >
                Get Started
              </Link>
            </Card>

            {/* Enterprise */}
            <Card className="flex flex-col">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-noir">Enterprise</h3>
                <div className="mt-2">
                  <span className="text-3xl font-medium text-noir">Custom</span>
                </div>
              </div>
              <ul className="space-y-2 mb-auto">
                {[
                  "Custom rate limits",
                  "Everything in Pro",
                  "Dedicated support",
                  "SLA agreement",
                  "Custom integrations",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-noir/80">
                    <svg className="w-4 h-4 text-cellar shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/contact-enterprise" className="block min-h-11 text-center px-4 py-3 border border-barrel text-barrel text-sm font-medium rounded-md hover:bg-barrel/5 transition-colors no-underline">
                Contact Us
              </Link>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
